import { useCallback, useEffect } from 'react';

/**
 * Загальна логіка закриття модалок/Drawer:
 * - ESC закриває (`onDismiss`).
 * - Якщо `dirty=true`, перед закриттям просимо підтвердження.
 * - Якщо модалка не відкрита (`enabled=false`), нічого не робимо.
 *
 * Повертає `tryDismiss` — функцію, яку треба викликати з оверлею
 * або кнопки «Скасувати». Сам `onDismiss` викликати напряму не варто,
 * щоб не пропустити confirm.
 */
export function useDismissable({
  enabled,
  dirty,
  onDismiss,
  confirmMessage = 'Є незбережені зміни. Закрити без збереження?',
}: {
  enabled: boolean;
  dirty: boolean;
  onDismiss: () => void;
  confirmMessage?: string;
}): { tryDismiss: () => void } {
  const tryDismiss = useCallback(() => {
    if (dirty && !window.confirm(confirmMessage)) return;
    onDismiss();
  }, [dirty, onDismiss, confirmMessage]);

  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        tryDismiss();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, tryDismiss]);

  return { tryDismiss };
}
